<template>
  <div
    class="order-list-item-wrapper"
    @click="showOrderDetail"
  >
    <Transition name="alert">
      <div
        v-if="alertVisible"
        class="alert-box-wrapper"
        @click.stop
      >
        <img
          src="@/assets/H5/order/alert-bg.png"
          height="56px"
          width="100%"
          alt=""
        >
        <div class="text">
          If not paid within 24 hours, the order will be automatically canceled.
        </div>
      </div>
    </Transition>
    <div
      v-if="orderStatus === 0 || orderStatus === 20 && data.b2bPaymentOrderVo?.method === 'BANK_TRANSFER_B2B'"
      class="count-down-time"
    >
      <div class="count-down-time-content">
        <div
          class="end-icon"
          @click.stop="showAlert"
        >
          End In
          <img src="@/assets/images/explain-fill.svg">
        </div>
        <Countdown
          v-if="orderStatus === 0"
          :time="new Date(data?.b2bOrderVo?.createTime).getTime() + 24 * 60 * 60 * 1000"
        />
        <Countdown
          v-if="orderStatus === 20 && data.b2bPaymentOrderVo.method === 'BANK_TRANSFER_B2B'"
          :time="new Date(data?.b2bPaymentOrderVo.bankTransferVO?.auditTime).getTime() + 24 * 60 * 60 * 1000"
        />
      </div>
    </div>
    <div
      v-if="orderStatus === 0 || orderStatus === 20 && data.b2bPaymentOrderVo?.method === 'BANK_TRANSFER_B2B'"
      style="height: 36px;"
    />
    <div class="order-list-item">
      <div class="header">
        <div class="header-time">
          <img
            src="@/assets/H5/order/timer.svg"
            alt=""
          >
          <span>{{ data?.b2bOrderVo?.createTimeStr }}</span>
        </div>
        <div
          class="header-status"
          :class="`header-status--${orderStatus}`"
        >
          {{ orderStatusText }}
        </div>
      </div>
      <div class="content">
        <div
          class="sku-list"
          @touchend.stop
        >
          <Image
            @click.stop="toDetailPage(item)"
            v-for="item in data.b2bOrderDetails"
            :key="item.skuId"
            class="sku"
            :src="item.skuImage"
          />
        </div>
        <div class="total-price">
          <div>Total </div>
          <div>{{ data?.b2bOrderVo.currencyCode.slice(0, 2) }} {{ data?.b2bOrderVo.currencyUnit }}{{ data?.b2bOrderVo?.totalAmount }}</div>
        </div>
      </div>
      <div class="order-info">
        <span>Total {{ data?.b2bOrderVo?.productQuantity }} items</span>
        <span />
        <span>{{ orderStatusText === 'Awaiting Payment' ? 'Awaiting Payment:' : 'Actual Payment:' }}</span>
        <div class="order-info__price">
          <IntlCurrency
            currency="USD"
          />
          {{ orderStatusText === 'Awaiting Payment' ? data.b2bOrderVo.totalAmountUsd : data.b2bOrderVo.actualTotalAmountUsd }}
        </div>
      </div>
      <!-- 支付失败 -->
      <div
        v-if="orderStatus === ORDER_STATUS.PAY_FAIL"
        class="error-info"
      >
        <img
          src="@/assets/H5/order/warning.svg"
          width="14px"
          alt=""
        >
        Payment confirmation failed, Please pay again.
      </div>
      <div
        v-if="orderStatus === ORDER_STATUS.CANCELLED"
        class="warning-info"
      >
        <img
          src="@/assets/images/icon-toast-warning.svg"
          alt=""
        >
        The transaction is closed.
      </div>
      <div class="button-list">
        <Button
          v-if="orderStatus === ORDER_STATUS.PLACE_ORDER"
          class="button"
          @click.stop="showCancelOrderDialog"
          type="text"
          :block="false"
        >
          Cancel
        </Button>
        <Button
          @click.stop="handleBuyAgain"
          class="button"
          :type="[0, 10, 20].includes(orderStatus) ? 'default' : 'primary'"
          :block="false"
        >
          Repeat Order
        </Button>
        <Button
          v-if="orderStatus === ORDER_STATUS.PLACE_ORDER"
          class="button"
          @click.stop="showPayNowDialog"
          type="primary"
          :block="false"
        >
          Pay Now
        </Button>
        <Button
          v-if="orderStatus === ORDER_STATUS.PAY_CONFIRMING"
          @click.stop="showConfirmOrderDialog"
          class="button"
          type="primary"
          :disabled="data.b2bPaymentOrderVo?.method === 'PAYPAL_STANDARD_B2B'"
          :block="false"
        >
          Confirming
        </Button>
        <Button
          v-if="orderStatus === ORDER_STATUS.PAY_FAIL"
          class="button"
          @click.stop="showPayAgainDialog"
          type="primary"
          :block="false"
        >
          Pay Again
        </Button>
      </div>
    </div>

    <!-- 遮罩层 -->
    <div
      v-if="maskVisible"
      @click.stop="closeMask"
      @touchmove.stop="closeMask"
      class="mask"
    />
  </div>
</template>
<script setup>
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import { Countdown } from '@/components/Countdown'
import { fetchData } from '@/utils/fetch'
import { useRouter } from 'vue-router'
import { gaAnalytics, buriedPointUtil } from '@/utils/utils'
import { Image } from '@/components/Image'
import { useCartStore } from '@/pinia/cart'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { Button } from '@/components/Button'

const ORDER_STATUS = Object.freeze({
  PLACE_ORDER: 0, // 下单成功待支付
  PAY_CONFIRMING: 10, // 支付确认中
  PAY_FAIL: 20, // 支付失败
  PAY_COMPLETE: 30, // 支付完成
  IN_PROCESS: 40, // 采购中
  SHIPPED: 50, // 已发货
  DELIVERED: 90, // 已送达
  CANCELLED: 99 // 已取消
})

const emits = defineEmits(['cancel', 'payNow', 'confirm', 'showDetail', 'repay'])

const props = defineProps({
  data: {
    type: Object,
    default: () => {}
  }
})

const router = useRouter()

const cartStore = useCartStore()

const orderStatus = computed(() => {
  return props.data.b2bOrderVo.status
})

const orderStatusText = computed(() => {
  if ([0, 10, 20].includes(orderStatus.value)) {
    return 'Awaiting Payment'
  } else if ([30, 40, 50].includes(orderStatus.value)) {
    return 'Fulfillment'
  } else {
    return 'Completed'
  }
})

const alertVisible = ref(false)
const showAlert = () => {
  alertVisible.value = true
  showMask()
}

// 遮罩层相关逻辑
const maskVisible = ref(false)
const showMask = () => {
  maskVisible.value = true
}
const closeMask = () => {
  maskVisible.value = false
  alertVisible.value = false
}

const showOrderDetail = () => {
  emits('showDetail')
}

// 确认取消订单弹窗
const showCancelOrderDialog = () => {
  gaAnalytics('mp_cancel_order_click', 'mp_cancel_order_click')
  buriedPointUtil({ eventId: 'MPOP205' })
  emits('cancel')
}
// 选择支付
const showPayNowDialog = () => {
  buriedPointUtil({ eventId: 'MPOP201' })
  emits('payNow')
}

const showPayAgainDialog = () => {
  gaAnalytics('mp_pay_again_click', 'mp_pay_again_click')
  buriedPointUtil({ eventId: 'MPOP203' })
  if (props.data.b2bOrderVo.totalAmountUsd > 200) {
    emits('repay')
  } else {
    emits('payNow')
  }
}

// 订单确认
const showConfirmOrderDialog = () => {
  if (props.data.b2bPaymentOrderVo?.method === 'PAYPAL_STANDARD_B2B') return
  gaAnalytics('mp_confirming_click', 'mp_confirming_click')
  buriedPointUtil({ eventId: 'MPOP202' })
  emits('confirm')
}

const handleBuyAgain = async () => {
  gaAnalytics('mp_buy_again_click', 'mp_buy_again_click')
  buriedPointUtil({ eventId: 'MPOP204' })
  const { code, data } = await fetchData('/appb/b2b/cart/reBuyOrder', {
    method: 'POST',
    body: JSON.stringify({
      orderNo: props.data.b2bOrderVo.orderNo
    })
  })
  if (code === 200) {
    fetchData('/appb/b2b/cart/cartCount').then(res => {
      cartStore.setCartCount(res.data)
    })

    sessionStorage.setItem('carts', JSON.stringify(data))
    localStorage.setItem('buy-again', true)
    router.push('/carts')
  }
}

const toDetailPage = (item) => {
  window.open('/offer/' + item.spuId)
}

onMounted(() => {
})

onBeforeMount(() => {
})
</script>
<style lang="less" scoped>
.order-list-item-wrapper {
  position: relative;
  .alert-box-wrapper {
    position: absolute;
    top: 30px;
    left: 12px;
    width: 266px;
    height: 56px;
    z-index: 10;
    overflow: hidden;
    .text {
      position: absolute;
      bottom: 0;
      width: 100%;
      height: 48px;
      color: white;
      font-size: 11px;
      font-family: mon-500;
      padding: 0 16px;
      line-height: 16px;
      display: flex;
      align-items: center;
    }
  }
  .count-down-time {
    position: absolute;
    top: 0px;
    width: 100%;
    height: 48px;
    background-color: #fff9e0;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    .count-down-time-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 48px;
      height: 36px;
      padding: 0 12px;

    }
    .end-icon {
      display: flex;
      align-items: center;
      gap: 2px;
      color: #232323;
      font-size: 12px;
      font-family: mon-600;
    }
  }
}
.order-list-item {
  position: relative;
  padding: 14px 12px;
  background-color: white;
  border-radius: 8px;
  margin-bottom: 8px;
  z-index: 2;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    font-family: mon-500;

    .header-time {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #232323;
    }

    .header-status {
      color: #232323;

      &--20 {
        color: #7A0505;
      }

      &--99 {
        color: #5C2F04;
      }
    }
  }

  .content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    margin-top: 12px;
    background-color: #f8f8f8;
    border-radius: 6px;

    .sku-list {
      display: flex;
      gap: 6px;
      width: 100%;
      overflow: scroll;

      &::-webkit-scrollbar {
        display: none;
      }

      .sku {
        flex-shrink: 0;
        width: 52px;
        height: 52px;
        background-color: white;
        border-radius: 4px;
        overflow: hidden;
      }
    }

    .total-price {
      text-align: right;
      font-size: 12px;
      line-height: 16px;
      margin-left: 24px;

      div:first-child {
        color: #6e717a;
        font-family: mon-500;
      }

      div:last-child {
        margin-top: 2px;
        color: #232323;
        font-family: mon-600;
        white-space: nowrap;
      }
    }
  }

  .error-info, .warning-info {
    display: flex;
    align-items: center;
    height: 32px;
    padding: 0 8px;
    margin-top: 12px;
    font-size: 12px;
    font-family: mon-500;
    white-space: nowrap;

    img {
      margin-right: 6px;
      width: 16px;
      height: 16px;
    }
  }

  .error-info {
    border: 1px solid #F7E3E8;
    border-radius: 4px;
    background-color: #FCF2F5;
    color: #7A0505;
  }

  .warning-info {
    border: 1px solid #F4E5D9;
    border-radius: 4px;
    background-color: #FAF4EF;
    color: #5C2F04;
  }

  .order-info {
    display: flex;
    align-items: center;
    justify-content: end;
    margin-top: 12px;
    white-space: nowrap;

    span:nth-child(1) {
      color: #6e717a;
      font-family: mon-500;
      font-size: 12px;
    }

    span:nth-child(2) {
      height: 8px;
      width: 1px;
      background-color: var(--light-split-color);
      margin: 0 8px;
    }

    span:nth-child(3) {
      color: #6E717A;
      font-family: mon-500;
      font-size: 12px;
      margin-right: 4px;
    }

    &__price {
      display: flex;
      align-items: center;
      font-size: 14px;
      color: #052785;
      font-family: mon-700;
    }
  }

  .button-list {
    display: flex;
    align-items: center;
    justify-content: end;
    margin-top: 12px;

    .button {
      padding: 0 16px;
      border-radius: 16px;
      font-size: 12px;
      margin-left: 8px;
      height: 32px;
      white-space: nowrap;
    }
  }
}
.mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0);
  z-index: 9;
}

.alert-enter-active {
  transform-origin: 50px 0;
  animation: alert 0.3s;
}

.alert-leave-active {
  transform-origin: 50px 0;
  animation: alert 0.3s reverse;
}

@keyframes alert {
  0% {
    transform: scale(0, 0);
    opacity: 0;
  }
  100% {
    transform: scale(1, 1);
    opacity: 1;
  }
}

</style>
